import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Check, X, Clock, User, Phone, Target, ShieldQuestion,
  UserCheck, AlertTriangle, HelpCircle, KeyRound,
} from 'lucide-react';
import { Modal, useModalClose } from '@/components/ui/Modal';
import type { ReactNode } from 'react';
import { api } from '@/utils/api';
import { DealerAccessRequestsSection } from './DealerAccessRequestsSection';

/* ------------------------------------------------------------------ */
/*  Types — mirror the enriched GET /lead-capture/customer-access-      */
/*  requests payload (BE1). Raw row columns are spread at the top level  */
/*  alongside the resolved requestedBy / existingLead / nameMatch.       */
/* ------------------------------------------------------------------ */

import { STAGE_LABELS, fullName, FILTERS, type AccessStatus, type NameVerdict, type PersonRef, type ExistingLead, type NameMatch, type AccessRequest } from './accessRequestsTypes';
import { useAuthStore } from '@/stores/auth';

// 2026-10-03 (Manak): Approve / Reject only for the roles the server's decide
// route allows — PATCH /lead-capture/customer-access-requests/:id →
// requireRole('admin','owner','super_admin','operations_manager'). Everyone
// else who reaches this page (manager, sales_manager) reads only.
const ACCESS_REQUEST_DECIDERS = new Set(['admin', 'owner', 'super_admin', 'operations_manager']);
export function canDecideCustomerAccessRequests(role: string | null | undefined): boolean {
  return ACCESS_REQUEST_DECIDERS.has(String(role ?? '').trim().toLowerCase());
}

export function AccessRequestsPage() {
  const navigate = useNavigate();
  const canDecide = canDecideCustomerAccessRequests(useAuthStore((s) => s.user?.role?.name));
  const [requests, setRequests] = useState<AccessRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>('pending');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [noteModal, setNoteModal] = useState<{ id: string; action: 'approve' | 'reject' } | null>(null);
  // Approve hands a lead over: ask first, and say who it moves from and to.
  const [confirmApprove, setConfirmApprove] = useState<AccessRequest | null>(null);
  const [noteText, setNoteText] = useState('');

  const fetchRequests = async () => {
    try {
      setLoading(true);
      setError(null);
      const params: Record<string, string> = {};
      if (filter) params.status = filter.toUpperCase();
      const res = await api.get('/lead-capture/customer-access-requests', { params });
      if (!Array.isArray(res.data?.data?.requests)) throw new Error('Invalid access requests');
      setRequests(res.data.data.requests);
    } catch {
      setError('Could not load access requests. Please try again.');
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRequests(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filter]);

  // Approving only changes the lead's owner (and its deal / contact owner). The
  // lead's stage is never touched - business decision N1 (Manak, 2026-10-18).
  const handleAction = async (
    id: string,
    action: 'approve' | 'reject',
    note?: string,
    // The owner the approver was shown; the server refuses if the lead moved since.
    expectedOwnerId?: string | null,
  ) => {
    try {
      setActionLoading(id);
      await api.patch(`/lead-capture/customer-access-requests/${id}`, {
        action,
        decisionNote: note || undefined,
        ...(action === 'approve' && expectedOwnerId !== undefined ? { expectedOwnerId } : {}),
      });
      setNoteModal(null);
      setConfirmApprove(null);
      setNoteText('');
      fetchRequests();
    } catch (err: any) {
      const code = err?.response?.data?.code;
      if (code === 'ALREADY_DECIDED') {
        alert('This request was already decided by someone else. Refreshing.');
        setNoteModal(null);
        setConfirmApprove(null);
        fetchRequests();
      } else if (code === 'OWNER_CHANGED') {
        alert('The lead now has a different owner than the one shown. Refreshing - please review and decide again.');
        setConfirmApprove(null);
        fetchRequests();
      } else if (code === 'NOTE_REQUIRED') {
        alert('Please give a reason to reject this request.');
      } else if (code === 'LEAD_UNAVAILABLE') {
        alert('Could not resolve a lead for this phone number — the request was left pending. Reject it with a reason instead.');
      } else {
        alert(`Failed to ${action} the request. Please try again.`);
      }
    } finally {
      setActionLoading(null);
    }
  };

  const statusBadge = (status: AccessStatus) => {
    switch (status) {
      case 'PENDING':
        return <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-700"><Clock size={12} /> Pending</span>;
      case 'APPROVED':
        return <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700"><Check size={12} /> Approved</span>;
      case 'REJECTED':
        return <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-700"><X size={12} /> Rejected</span>;
      default:
        return <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">{status}</span>;
    }
  };

  const stageChip = (stage: string | null) => {
    if (!stage) return null;
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-[#1F4B3A]/10 px-2 py-0.5 text-[11px] font-medium text-[#1F4B3A]">
        <Target size={10} />
        {STAGE_LABELS[stage] || stage}
      </span>
    );
  };

  /**
   * Duplicate-confidence badge. Phone (last-10) is identity; the name is only
   * advisory. Surfaces BOTH names when the verdict is not a clean "same" so the
   * approver can judge typo-vs-different-person.
   */
  const confidenceBadge = (nm: NameMatch | null) => {
    if (!nm) return null;
    const both = (
      <span className="text-[11px] text-gray-500">
        “{nm.existingName || '—'}” vs “{nm.typedName || '—'}”
      </span>
    );
    switch (nm.verdict) {
      case 'same':
        return (
          <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700">
            <UserCheck size={12} /> Same person
          </span>
        );
      case 'likely_typo':
        return (
          <span className="inline-flex flex-wrap items-center gap-1.5">
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-700">
              <AlertTriangle size={12} /> Likely typo ({Math.round(nm.ratio * 100)}%)
            </span>
            {both}
          </span>
        );
      case 'different':
        return (
          <span className="inline-flex flex-wrap items-center gap-1.5">
            <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-700">
              <ShieldQuestion size={12} /> Names differ
            </span>
            {both}
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-500">
            <HelpCircle size={12} /> No name given
          </span>
        );
    }
  };

  const fmtDate = (d: string | null) =>
    d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

  return (
    <div className="mx-auto max-w-7xl p-4 sm:p-6">
      {/* Header */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#1F4B3A]/10 text-[#1F4B3A]">
            <KeyRound size={20} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Access Requests</h1>
            <p className="mt-0.5 text-sm text-gray-500">
              Reps requesting access to a lead already in the system. Approving reassigns the lead to the requester.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {FILTERS.map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                filter === f.key
                  ? 'bg-[#1F4B3A] text-white shadow-sm'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Sales Round B — dealer access requests (lead rows below are unchanged) */}
      <DealerAccessRequestsSection status={filter} />

      {/* States */}
      {loading ? (
        <div className="flex h-64 items-center justify-center text-gray-400">Loading requests…</div>
      ) : error ? (
        <div className="flex h-64 flex-col items-center justify-center gap-3 text-gray-500">
          <AlertTriangle size={40} className="text-amber-400" />
          <p>{error}</p>
          <button
            onClick={fetchRequests}
            className="rounded-lg bg-[#1F4B3A] px-4 py-2 text-sm font-medium text-white hover:bg-[#173a2d]"
          >
            Retry
          </button>
        </div>
      ) : requests.length === 0 ? (
        <div className="flex h-64 flex-col items-center justify-center text-gray-400">
          <KeyRound size={48} className="mb-2 opacity-30" />
          <p>No {filter || ''} access requests</p>
        </div>
      ) : (
        <div className="space-y-4">
          {requests.map(req => {
            const requesterName = fullName(req.requestedBy);
            const ownerName = req.existingLead?.assignedToName;
            // 🔒 2026-08-31 (Manak) — this compares the CURRENT owner, and an
            // approval assigns the lead to the requester. So on any approved
            // request it was always true, and the card told Manak "Already
            // theirs" about a decision he had already made — describing the
            // outcome, not what he was being asked.
            //
            // On a PENDING request the comparison is honest: the lead is theirs
            // right now and the request should not exist. Only show it there.
            const isSelfRequest =
              req.status === 'PENDING' &&
              !!req.existingLead?.assignedToId &&
              req.existingLead.assignedToId === req.requestedById;
            return (
              <div key={req.id} className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  {/* Left: main info */}
                  <div className="min-w-0 flex-1">
                    <div className="mb-2 flex flex-wrap items-center gap-2">
                      {statusBadge(req.status)}
                      {confidenceBadge(req.nameMatch)}
                      {isSelfRequest && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2.5 py-0.5 text-xs font-medium text-indigo-600">
                          Already theirs
                        </span>
                      )}
                      <span className="text-xs text-gray-400">{fmtDate(req.createdAt)}</span>
                    </div>
                    {/* 2026-10-03 — kind chip; whose it is when a coordinator raised it for her manager. */}
                    <div className="mb-2 flex flex-wrap items-center gap-1.5">
                      <span className="rounded-full bg-sky-50 px-2 py-0.5 text-xs font-semibold text-sky-700">Lead access</span>
                      {req.raisedBy && (
                        <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700">For {requesterName}</span>
                      )}
                      {req.raisedBy && (
                        <span className="text-xs text-gray-500">Raised by {fullName(req.raisedBy)}</span>
                      )}
                    </div>

                    {/* Requester + phone */}
                    <h3 className="flex flex-wrap items-center gap-x-2 gap-y-1 text-lg font-semibold text-gray-900">
                      <span className="inline-flex items-center gap-1.5">
                        <User size={16} className="text-[#E07A16]" />
                        {requesterName}
                      </span>
                      <span className="text-gray-300">·</span>
                      <span className="inline-flex items-center gap-1.5 text-base font-medium text-gray-600">
                        <Phone size={14} className="text-gray-400" />
                        {req.customerMobile}
                      </span>
                    </h3>
                    {req.requestedBy?.email && (
                      <p className="mt-0.5 text-xs text-gray-400">{req.requestedBy.email}</p>
                    )}

                    {/* Rep's typed name for the lead */}
                    {req.typedName && (
                      <p className="mt-1.5 text-sm text-gray-600">
                        Rep entered lead name: <span className="font-medium text-gray-800">{req.typedName}</span>
                      </p>
                    )}

                    {/* Existing lead + current owner + stage */}
                    <div className="mt-3 rounded-lg bg-gray-50 p-3">
                      {req.existingLead ? (
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm">
                          <span className="inline-flex items-center gap-1.5 text-gray-700">
                            <Target size={14} className="text-[#1F4B3A]" />
                            Existing lead:
                            <span className="font-medium text-gray-900">{req.existingLead.name || '(unnamed)'}</span>
                          </span>
                          {stageChip(req.existingLead.canonicalStage)}
                          <span className="inline-flex items-center gap-1.5 text-gray-700">
                            <UserCheck size={14} className="text-purple-500" />
                            Owner:
                            <span className="font-medium text-gray-900">{ownerName || 'Unassigned'}</span>
                          </span>
                        </div>
                      ) : (
                        <p className="text-sm italic text-gray-500">
                          No existing lead resolved for this number (a plain phone match will be attempted on approve).
                        </p>
                      )}
                    </div>

                    {/* Reason from the rep */}
                    {req.reason && (
                      <p className="mt-2 text-sm text-gray-600">
                        Reason: <span className="text-gray-700">{req.reason}</span>
                      </p>
                    )}

                    {/* Decision trail */}
                    {req.status !== 'PENDING' && (
                      <div className="mt-2 text-sm">
                        <span className={req.status === 'APPROVED' ? 'font-medium text-green-700' : 'font-medium text-red-700'}>
                          {req.status === 'APPROVED' ? 'Approved' : 'Rejected'}
                          {req.decidedBy ? ` by ${fullName(req.decidedBy)}` : ''}
                        </span>
                        {req.decidedAt && <span className="ml-1.5 text-xs text-gray-400">on {fmtDate(req.decidedAt)}</span>}
                        {req.decisionNote && (
                          <div className="mt-1.5 rounded bg-blue-50 px-3 py-1.5 text-blue-700">
                            <span className="font-medium">Note:</span> {req.decisionNote}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Deep-link to the existing lead */}
                    {req.existingLead && (
                      <button
                        onClick={() => navigate('/leads', { state: { leadId: req.existingLead!.id } })}
                        className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-[#1F4B3A] hover:underline"
                      >
                        <Target size={12} /> View lead
                      </button>
                    )}
                  </div>

                  {/* Right: actions */}
                  {canDecide && req.status === 'PENDING' && (
                    <div className="flex shrink-0 flex-row gap-2 sm:flex-col">
                      <button
                        onClick={() => setConfirmApprove(req)}
                        disabled={actionLoading === req.id}
                        className="flex items-center justify-center gap-1.5 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-green-700 disabled:opacity-50"
                      >
                        <Check size={14} /> {actionLoading === req.id ? 'Working…' : 'Approve'}
                      </button>
                      <button
                        onClick={() => { setNoteModal({ id: req.id, action: 'reject' }); setNoteText(''); }}
                        disabled={actionLoading === req.id}
                        className="flex items-center justify-center gap-1.5 rounded-lg bg-red-50 px-4 py-2 text-sm font-medium text-red-600 transition-colors hover:bg-red-100 disabled:opacity-50"
                      >
                        <X size={14} /> Reject
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Approve confirmation: approving moves the lead to the requester. */}
      {confirmApprove && (
        <Modal open onClose={() => setConfirmApprove(null)} ariaLabel="Approve access request" className="!max-w-md !bg-white p-6">
          <div>
            <h3 className="mb-3 text-lg font-semibold text-gray-900">Approve access request?</h3>
            <p className="mb-1 text-sm text-gray-600">
              {confirmApprove.existingLead?.name || confirmApprove.customerMobile} will move from{' '}
              <span className="font-medium text-gray-900">{confirmApprove.existingLead?.assignedToName || 'Unassigned'}</span> to{' '}
              <span className="font-medium text-gray-900">{fullName(confirmApprove.requestedBy)}</span>.
            </p>
            <p className="text-sm text-gray-500">The lead&apos;s stage stays as it is.</p>
            <div className="mt-4 flex justify-end gap-2">
              <CancelBtn className="rounded-lg px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100">
                Cancel
              </CancelBtn>
              <button
                onClick={() => handleAction(
                  confirmApprove.id, 'approve', undefined,
                  confirmApprove.existingLead ? (confirmApprove.existingLead.assignedToId ?? null) : undefined,
                )}
                disabled={actionLoading === confirmApprove.id}
                className="rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
              >
                {actionLoading === confirmApprove.id ? 'Processing…' : 'Confirm Approve'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Reject-note modal (reject requires a note) */}
      {noteModal && (
        <Modal open onClose={() => setNoteModal(null)} ariaLabel="Reject access request" className="!max-w-md !bg-white p-6">
          <div>
            <h3 className="mb-3 text-lg font-semibold text-gray-900">Reject access request</h3>
            <p className="mb-3 text-sm text-gray-500">
              Tell the rep why (e.g. “different person — re-check the number”). This note is sent to them.
            </p>
            <textarea
              value={noteText}
              onChange={e => setNoteText(e.target.value)}
              placeholder="Reason for rejection…"
              autoFocus
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#1F4B3A] focus:outline-none focus:ring-1 focus:ring-[#1F4B3A]"
              rows={3}
            />
            <div className="mt-4 flex justify-end gap-2">
              <CancelBtn className="rounded-lg px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100">
                Cancel
              </CancelBtn>
              <button
                onClick={() => handleAction(noteModal.id, 'reject', noteText.trim())}
                disabled={actionLoading === noteModal.id || !noteText.trim()}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                {actionLoading === noteModal.id ? 'Processing…' : 'Confirm Reject'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function CancelBtn({ className, children }: { className?: string; children: ReactNode }) {
  const { requestClose } = useModalClose();
  return <button type="button" onClick={requestClose} className={className}>{children}</button>;
}
